import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import { getVisibleFilters, useFiltersV2Select, useFiltersV2Store } from "PFApp/components/filters_v2";
import { Accordion } from "PFComponents/accordion";
import { Stack } from "PFComponents/containers";
import { LoadingDots } from "PFComponents/loading_dots";
import { useTranslation } from "react-i18next";

import { FiltersV2List } from "../../../parts/filters_v2_list";
import { FiltersV2PanelProps } from "../filters_v2_panel";
import css from "../filters_v2_panel.module.scss";
import { FiltersV2AutoRefreshWarning } from "./filters_v2_auto_refresh_warning";
import { FiltersV2ChildrenAccordion } from "./filters_v2_children_accordion";

type FiltersV2PanelContentProps = FiltersV2PanelProps & {
  lastFocusedFilterNameRef: React.MutableRefObject<string | null>;
};

export const FiltersV2PanelContent = ({
  omit: excludedFilters = {},
  lastFocusedFilterNameRef,
  labels,
  childrenToggleInfo
}: FiltersV2PanelContentProps) => {
  const { areLoading } = useFiltersV2Select("areLoading");
  const { t } = useTranslation("filters", { keyPrefix: "panel" });

  const mainFilters = useFiltersV2Store((state) =>
    omit(getVisibleFilters(state.buffer.mainFilters), excludedFilters?.main || [])
  );
  const childrenFilters = useFiltersV2Store((state) =>
    omit(getVisibleFilters(state.buffer.childrenFilters), excludedFilters?.children || [])
  );

  if (areLoading) {
    return (
      <div className={css.loadingOverlay}>
        <LoadingDots circlesEnabled circleSize="lg" />
      </div>
    );
  }

  if (isEmpty(childrenFilters)) {
    return (
      <Stack>
        <FiltersV2AutoRefreshWarning />
        <FiltersV2List lastFocusedFilterNameRef={lastFocusedFilterNameRef} filters={mainFilters} />
      </Stack>
    );
  }

  return (
    <Stack>
      <FiltersV2AutoRefreshWarning />
      <Accordion header={labels?.main || t("title")} animated={false}>
        <FiltersV2List lastFocusedFilterNameRef={lastFocusedFilterNameRef} filters={mainFilters} />
      </Accordion>
      <FiltersV2ChildrenAccordion
        filters={childrenFilters}
        label={labels?.children}
        childrenToggleInfo={childrenToggleInfo}
        lastFocusedFilterNameRef={lastFocusedFilterNameRef}
      />
    </Stack>
  );
};
